import React from "react";
import { Link, useLocation } from "react-router-dom";
import { RadialBar, RadialBarChart, PolarAngleAxis, ResponsiveContainer } from "recharts";

export const RAG = {
  RED: "#D32F2F",
  AMBER: "#F57C00",
  YELLOW: "#FBC02D",
  GREEN: "#388E3C",
  TEAL: "#00897B",
  NAVY: "#003366",
  BLUE: "#008080",
};

export function ragForPct(pct) {
  const v = Number(pct || 0);
  if (v >= 85) return RAG.GREEN;
  if (v >= 75) return RAG.TEAL;
  if (v >= 60) return RAG.YELLOW;
  if (v >= 40) return RAG.AMBER;
  return RAG.RED;
}

// --- Enums (mirror backend/models/ropa.py) ---------------------------------
export const STATUSES = ["Draft", "InReview", "Approved", "Stale", "Retired"];
export const PURPOSE_CATEGORIES = [
  "ServiceDelivery", "LegalObligation", "Employment", "Marketing",
  "Security", "Analytics", "Other",
];
export const PRINCIPAL_CATEGORIES = ["Customers", "Employees", "Vendors", "Children", "Other"];
export const VOLUME_BANDS = ["<1L", "1L-10L", "10L-50L", "50L-2Cr", ">2Cr"];
export const SENSITIVITIES = ["Low", "Medium", "High"];
export const LAWFUL_BASES = ["Consent", "CertainLegitimateUse"];
export const LEGITIMATE_USE_GROUNDS = [
  "VoluntarilyProvided", "StateBenefit", "LegalObligation",
  "MedicalEmergency", "Employment", "PublicOrder",
];
export const ERASURE_TRIGGERS = [
  "PurposeExpiry", "ConsentWithdrawal", "AccountClosure", "StatutoryPeriodLapse", "None",
];
export const THIRD_SCHEDULE_CLASSES = ["ECommerce", "OnlineGaming", "SocialMedia", "NotApplicable"];
export const SECURITY_MEASURES = [
  "Encryption", "Masking", "Tokenisation", "AccessControl",
  "Logging", "Backups", "ContractualProcessorControls",
];
export const VERIFIABLE_CONSENT_METHODS = ["DFHeldDetails", "VoluntaryDetails", "DigitalLockerToken"];
export const FOURTH_SCHEDULE_EXEMPTIONS = ["None", "Healthcare", "Education", "Childcare", "Transport"];
export const DPIA_STATUSES = ["NotRequired", "NotStarted", "InProgress", "Completed"];
export const ASSET_TYPES = [
  "Application", "SaaS", "Database", "CloudStorage", "FileShare", "PhysicalStorage", "Endpoint", "Other",
];
export const HOSTINGS = ["OnPrem", "CloudIndia", "CloudOverseas", "Hybrid"];
export const PARTY_TYPES = [
  "DataProcessor", "Recipient", "Affiliate", "OtherFiduciary", "ConsentManager",
];
export const DPA_STATUSES = ["Signed", "Pending", "NotRequired", "Missing"];
export const RISK_RATINGS = ["Low", "Medium", "High"];

export const WORKSPACE_TABS = [
  { value: "findings", label: "Findings Register" },
  { value: "controls", label: "Controls Tracker" },
  { value: "ropa", label: "RoPA" },
  { value: "evidence", label: "Evidence Vault" },
  { value: "remediation", label: "Remediation" },
  { value: "team-raci", label: "Team RACI" },
  { value: "trend", label: "Trend" },
  { value: "board-memo", label: "Board Memo" },
];

const STATUS_PILL = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  InReview: "bg-[#FBC02D]/15 text-[#8A6D00] border-[#FBC02D]/40",
  Approved: "bg-[#388E3C]/10 text-[#388E3C] border-[#388E3C]/30",
  Stale: "bg-[#F57C00]/10 text-[#F57C00] border-[#F57C00]/30",
  Retired: "bg-slate-50 text-slate-400 border-slate-200",
};

export const StatusPill = ({ status }) => (
  <span
    data-testid={`ropa-status-pill-${status}`}
    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${
      STATUS_PILL[status] || STATUS_PILL.Draft
    }`}
  >
    {status || "Draft"}
  </span>
);

export const CompletenessBar = ({ value }) => {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 rounded-full bg-slate-150 bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${v}%`, backgroundColor: ragForPct(v) }}
        />
      </div>
      <span className="text-[11px] font-mono text-slate-500 w-8 text-right">{v}%</span>
    </div>
  );
};

export const CoverageGauge = ({ value, size = 132 }) => {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const color = ragForPct(v);
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="ropa-coverage-gauge">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          innerRadius="72%"
          outerRadius="100%"
          data={[{ name: "coverage", value: v }]}
          startAngle={90}
          endAngle={-270}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar dataKey="value" cornerRadius={12} fill={color} background={{ fill: "#EEF2F7" }} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color }}>{v}%</span>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">coverage</span>
      </div>
    </div>
  );
};

export const CompletenessRing = ({ value, size = 104 }) => {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  const color = ragForPct(v);
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="ropa-completeness-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#EEF2F7" strokeWidth="9" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="9" fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * v) / 100}
          style={{ transition: "stroke-dashoffset 600ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold" style={{ color }}>{v}%</span>
        <span className="text-[10px] text-slate-400">complete</span>
      </div>
    </div>
  );
};

export const CitationChip = ({ text }) => (
  <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 align-middle">
    {text}
  </span>
);

export const WorkspaceNav = ({ assessmentId }) => {
  const location = useLocation();
  const active = location.pathname.split("/")[3];
  return (
    <nav
      data-testid="workspace-nav"
      className="bg-white border border-slate-200 rounded-xl p-1 flex flex-wrap gap-1"
    >
      {WORKSPACE_TABS.map((t) => (
        <Link
          key={t.value}
          to={`/assessment/${assessmentId}/${t.value}`}
          data-testid={`nav-${t.value}`}
          className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
            active === t.value
              ? "bg-[#003366] text-white"
              : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {t.label}
        </Link>
      ))}
    </nav>
  );
};

export const ComingLater = ({ label }) => (
  <div
    className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center"
    data-testid={`ropa-coming-later-${label}`}
  >
    <div className="text-sm font-medium text-slate-500">Coming in a later release</div>
    <div className="text-xs text-slate-400 mt-1">{label} is not part of this sprint.</div>
  </div>
);
